import { CACHE_STATUS_FRESH, createCacheClient } from '@noths/polaris-server-caching';

import type { ContentWithStatus } from '../types';
import { fetchContentGraphQLWithoutCaching } from './fetchContentGraphQLWithoutCaching';
import type { FetchContentGraphQlArgs, FetchContentGraphQlOptions } from './types';

const fetchPromises = {};

interface FetchContentGraphQLWithCachingOptions
  extends Omit<FetchContentGraphQlOptions, 'cacheKey' | 'cacheTTLInSeconds'> {
  // these are mandatory for this function
  cacheKey: string;
  cacheTTLInSeconds: number;
}

export const fetchContentGraphQLWithCaching = async (
  { deliveryToken, environment, query, stackAPIKey, variables }: FetchContentGraphQlArgs,
  { branch, cacheKey, cacheTTLInSeconds, region, timeout }: FetchContentGraphQLWithCachingOptions,
): Promise<ContentWithStatus> => {
  const fetchFn = () =>
    fetchContentGraphQLWithoutCaching(
      { deliveryToken, environment, query, stackAPIKey, variables },
      { branch, region, timeout },
    );

  const cacheClient = createCacheClient();

  // cacheClient will be undefined if REDIS env vars are missing or invalid
  const cachedEntry = await cacheClient?.getByKey(cacheKey, cacheTTLInSeconds);

  if (cachedEntry && cachedEntry.status === CACHE_STATUS_FRESH) {
    // cache hit
    return Promise.resolve({
      content: cachedEntry.data,
      status: 'fresh',
    });
  }

  if (fetchPromises[cacheKey]) {
    // fetch in progress
    return fetchPromises[cacheKey];
  }

  // cache miss or expired
  fetchPromises[cacheKey] = fetchFn()
    .then((content): ContentWithStatus => {
      delete fetchPromises[cacheKey];
      cacheClient?.setByKey(cacheKey, content);

      return {
        content: content,
        status: 'fresh',
      };
    })
    .catch((error): ContentWithStatus => {
      delete fetchPromises[cacheKey];

      if (cachedEntry) {
        return {
          content: cachedEntry.data,
          status: 'expired',
        };
      } else {
        return {
          content: error,
          status: 'error',
        };
      }
    });

  return fetchPromises[cacheKey];
};
